import {
  ReputationInsightSeverity,
  type ReputationInsightDto,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  cn,
  healthAttentionFgClass,
  healthAttentionSurfaceClass,
  healthInfoFgClass,
  healthInfoSurfaceClass,
  healthOkFgClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import {
  ArrowRight,
  CircleCheck,
  Info,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';

import { formatInsightText } from '@/core/components/reputation/overview/insight-text';
import { REPUTATION_SECTION_TITLE_CLASS } from '@/core/components/reputation/reputation-surface';

export type OverviewAttentionCardLabels = {
  title: string;
  openCount: string;
  allClear: string;
  openUrgent: string;
};

const SEVERITY: Record<
  ReputationInsightSeverity,
  { icon: LucideIcon; row: string; iconClass: string }
> = {
  [ReputationInsightSeverity.WARNING]: {
    icon: TriangleAlert,
    row: healthAttentionSurfaceClass,
    iconClass: healthAttentionFgClass,
  },
  [ReputationInsightSeverity.SUCCESS]: {
    icon: CircleCheck,
    row: healthOkSurfaceClass,
    iconClass: healthOkFgClass,
  },
  [ReputationInsightSeverity.INFO]: {
    icon: Info,
    row: healthInfoSurfaceClass,
    iconClass: healthInfoFgClass,
  },
};

export function OverviewAttentionCard({
  insights,
  templates,
  urgentCount,
  labels,
  onInsight,
  onOpenUrgent,
}: {
  insights: ReputationInsightDto[];
  templates: Record<string, string>;
  /** Critical reviews still waiting for a reply. */
  urgentCount: number;
  labels: OverviewAttentionCardLabels;
  onInsight: (insight: ReputationInsightDto) => void;
  onOpenUrgent: () => void;
}) {
  const openCount = insights.filter(
    (insight) => insight.severity === ReputationInsightSeverity.WARNING,
  ).length;

  return (
    <Card className="flex min-w-0 flex-col gap-3 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className={REPUTATION_SECTION_TITLE_CLASS}>{labels.title}</h2>
        {openCount > 0 ? (
          <span className="text-muted-foreground font-mono text-[11.5px]">
            {labels.openCount.replace('{count}', String(openCount))}
          </span>
        ) : null}
      </div>

      <ul className="flex flex-col gap-3">
        {insights.map((insight) => {
          const tone = SEVERITY[insight.severity];
          const Icon = tone.icon;
          const text = formatInsightText(templates, insight);
          const rowClass = cn(
            'flex w-full items-start gap-3 rounded-[var(--radius-nested)] border px-3.5 py-3 text-left text-[13px] leading-snug',
            tone.row,
          );
          const content = (
            <>
              <Icon
                className={cn('mt-px size-4 shrink-0', tone.iconClass)}
                aria-hidden
              />
              <span className="min-w-0 flex-1">{text}</span>
            </>
          );
          return (
            <li key={`${insight.key}-${JSON.stringify(insight.params)}`}>
              {insight.action ? (
                <button
                  type="button"
                  onClick={() => onInsight(insight)}
                  className={cn(
                    rowClass,
                    'min-h-11 cursor-pointer transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                >
                  {content}
                </button>
              ) : (
                <div className={rowClass}>{content}</div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-auto flex items-center gap-2 border-t pt-3 text-[12.5px]">
        {urgentCount === 0 ? (
          <span className="text-muted-foreground flex items-center gap-2">
            <CircleCheck
              className={cn('size-[15px]', healthOkFgClass)}
              aria-hidden
            />
            {labels.allClear}
          </span>
        ) : (
          <Button variant="ghostAccent" size="sm" onClick={onOpenUrgent}>
            {labels.openUrgent.replace('{count}', String(urgentCount))}
            <ArrowRight className="ml-1 size-3.5" aria-hidden />
          </Button>
        )}
      </div>
    </Card>
  );
}
